/**
 * Pure-static `BuiltinAliasFactory` exports for the rows-side builtin
 * alias templates (R6.2). Each factory bundles a `templateName` +
 * `template` value with no app-layer closure required — they only
 * read from `AliasProps`.
 *
 * Aliases that DO require an app-layer dependency live in / are
 * parameterized for the layer that owns it: `calendar-list` needs a
 * `CalendarConfigProvider` (`tento/tento-lona-js/src/preferences/calendar-aliases.ts`);
 * `task-list` needs a `TaskListConfigProvider` (the parameterized
 * `taskListAliasFactory` below, fed by the server's `:~lona:tasks:lists`
 * enumerator); `alias.secondary-timezones` needs a `LiveData` for
 * user prefs. None of these are in the pure-static default set.
 *
 * Callers compose factory arrays at `SheetModelCache` construction
 * time. Tests can use `defaultBuiltinAliasFactories()` (this module's
 * default set of pure-static templates) for hydration scenarios
 * that don't exercise calendar-list / task-list / preferences.
 */

import type { Option } from "../core/option.ts";
import { Rational } from "@tento-core/rational";

import type { AliasProps } from "./alias-templates.ts";
import type { AliasTemplate } from "./alias-templates.ts";
import type { ModelSheet } from "../sheet/rows-sheet.ts";
/**
 * A pure-static alias factory: a template name, its template, and an
 * optional per-sheet attach hook. Enables client-hydrated aliases (R6.2) —
 * instead of the rows-side declaring a per-domain provider interface
 * (preferences, feature flags, …), the layer above ships a factory that
 * closes over its own dependency and registers an alias template + change
 * listener.
 *
 * Each `SheetModelCache` accepts an array of these. Templates are
 * registered into a per-sheet overlay (consulted before the global
 * `ALIAS_TEMPLATES` registry); `onAttach(sheet)` is called once per
 * sheet. Dependency-driven re-expansion should be declared via
 * `AliasTemplate.reexpandOn` (wired generically by
 * `wireDeclaredReexpand`); hand-write `onAttach` only for behavior
 * beyond "re-expand when a declared path changes".
 *
 * Declared here, where it merges with the `BuiltinAliasFactory` namespace
 * below: `lib.ts` re-exports the name once and callers get both meanings.
 */
export interface BuiltinAliasFactory {
  readonly templateName: string;
  readonly template: AliasTemplate;
  /**
   * Optional per-sheet attach hook. Returns a cleanup function the
   * sheet calls when the factory's lifetime ends.
   */
  onAttach?(sheet: ModelSheet): Unsubscribe;
}
import type { CellValue } from "../core/dtype.ts";
import { Json } from "../core/json.ts";
import type { SheetRegistries } from "../sheet/sheet-registries.ts";
import { RowNetworkKey } from "../ids/row-network-key.ts";
import { RowTypeSpecOps, type ChildSpec } from "../rows/row-type-spec.ts";
import { parseRendererId } from "../rows/slot.ts";
import {
  type TaskListConfigProvider,
  taskListConfigCell,
} from "../providers/task-list-config-provider.ts";
import type { Unsubscribe } from "../backends/rows-backend.ts";

// ── Factories ──────────────────────────────────────────────────

function weatherAliasFactory(): BuiltinAliasFactory {
  return {
    templateName: "alias.weather",
    template: {
      displayName: "Weather",
      props: { locationId: "string" },
      // Default data address: querying the weather alias resolves to its
      // concrete `[weather@lona.so]:~weather:{lid}` source row. The key is
      // FULLY QUALIFIED at the point of production (data-address migration
      // decision #1) — no downstream layer prefixes it — and the `:~`
      // canonical shape routes the read to `canonicalCells`, so every
      // user's weather alias shares one fetch/store. First `DataRef`
      // adopter (data-address).
      dataRef: (props) => ({
        lookupKey: weatherLookupKey(props),
        localPath: null,
      }),
      // No `reexpandOn`: this template's location is prop-pinned
      // (`props.locationId`), so it has no live preference dependency.
      // The preference-tracking variant is `alias.default-weather`
      // (src/preferences/preferences-aliases.ts), which declares it.
      rule: {
        kind: "static",
        children: (props) => weatherAliasChildren(props),
      },
    },
  };
}

function weatherAliasChildren(props: AliasProps): ChildSpec[] {
  // An alias that names no location expands no source: no data is read for a
  // location nobody chose (operator ruling: nothing invented).
  if (weatherLookupKey(props).length === 0) return [];
  return [
    {
      preset: "weather",
      type: "data-source",
      label: "Weather",
      lookupKey: weatherLookupKey(props),
      position: Rational.nth(0),
      storage: "own",
      placement: "child",
      attributes: weatherSourceAttributes(props),
      children: [
        {
          preset: "renderer-column",
          type: "column.weather",
          position: Rational.nth(0),
          storage: "parent",
          placement: "child",
        },
      ],
    },
    {
      preset: "logs",
      type: "logs",
      label: "Sync Logs",
      lookupKey: weatherLogsLookupKey(props),
      position: Rational.nth(1),
      storage: "own",
      placement: "child",
      attributes: { logType: "weather" },
      // Virtual sources do not auto-hydrate default renderers. Declare the
      // log renderer here so weather keeps its typed sync-log presentation.
      children: [{
        preset: "logs-renderer-0",
        type: "column.logs",
        position: Rational.nth(0),
        storage: "parent",
        placement: "child",
      }],
    },
  ];
}

function timezoneAliasFactory(): BuiltinAliasFactory {
  return {
    templateName: "alias.timezone",
    template: {
      displayName: "Timezone",
      props: { tz: "string" },
      rule: {
        kind: "static",
        children: (props) => {
          const tz = optionalStringProp(props, "tz");
          return [
            {
              preset: "timezone",
              type: "data-source",
              label: "Timezone",
              position: Rational.nth(0),
              storage: "own",
              placement: "child",
              attributes: {
                dtype: "obj",
                ...(tz != null ? { tz } : {}),
              },
              children: [
                {
                  preset: "renderer-column",
                  type: "column.timezone",
                  position: Rational.nth(0),
                  storage: "parent",
                  placement: "child",
                },
                // Shared parent-legend-delegating renderer (R6.1).
                // Under a `timeline[column]` ancestor (calendar), this
                // slot is claimed; the cell is a no-op and the
                // calendar's own legend walker renders the tz badge.
                {
                  preset: "renderer-timeline-column",
                  type: "timeline[column].timeline-legend",
                  position: Rational.nth(1),
                  storage: "parent",
                  placement: "child",
                },
              ],
            },
          ];
        },
      },
    },
  };
}

function dataAliasFactory(): BuiltinAliasFactory {
  return {
    // NOTE: kept as bare `data` (not `alias.data`) — this synthesized
    // template shadows the `data` wire row type and is coupled to the
    // DataAliasPlugin's id; it is never wire-persisted. `displayName`
    // still gives it a proper property-panel label.
    templateName: "data",
    template: {
      displayName: "Data",
      props: { lookupKey: "string" },
      rule: {
        kind: "static",
        children: (props, ctx) => dataChildren(props, ctx.registries),
      },
    },
  };
}

function chartAliasFactory(): BuiltinAliasFactory {
  return {
    templateName: "alias.chart",
    template: {
      displayName: "Chart",
      rule: {
        kind: "static",
        children: () => chartChildren(),
      },
    },
  };
}

function taskAliasFactory(): BuiltinAliasFactory {
  return {
    templateName: "alias.task",
    template: {
      displayName: "Task",
      props: { authId: "string" },
      rule: {
        kind: "static",
        children: (props) => [
          {
            preset: "tasks",
            type: "alias",
            label: "Tasks",
            position: Rational.nth(0),
            storage: "own",
            placement: "child",
            attributes: {
              template: "alias.task-list",
              props: { authId: requireStringProp(props, "authId", "task") },
            },
          },
        ],
      },
    },
  };
}

/**
 * `task-list` alias — closes over an app-supplied `TaskListConfigProvider`
 * (the task analog of `calendarListAliasFactory`). The dynamic tail spawns
 * one `data-source` child per list the provider reports, in addition to the
 * static `:~lona:tasks` default. The app resolves the provider's lists from
 * the server's `:~lona:tasks:lists` enumerator. Not part of
 * `BuiltinAliasFactory.defaults()` — register it via
 * `builtinAliasFactories({ taskListConfig })` (an empty provider, as in
 * tests, yields just the default `:~lona:tasks` list).
 */
export function taskListAliasFactory(
  taskListConfig: TaskListConfigProvider,
): BuiltinAliasFactory {
  return {
    templateName: "alias.task-list",
    template: {
      displayName: "Tasks",
      props: { authId: "string" },
      rule: {
        kind: "static",
        children: [
          {
            preset: "tasks",
            type: "list",
            label: "Tasks",
            position: Rational.nth(0),
            storage: "own",
            placement: "child",
            attributes: { type: "list" },
            children: [
              {
                preset: "renderer-column",
                type: "column.task",
                position: Rational.nth(0),
                storage: "parent",
                placement: "child",
              },
              {
                preset: "renderer-timeline-row",
                type: "timeline[row].task",
                position: Rational.nth(1),
                storage: "parent",
                placement: "child",
              },
              {
                preset: "renderer-timeline-column",
                type: "timeline[column].task",
                position: Rational.nth(2),
                storage: "parent",
                placement: "child",
              },
              {
                preset: "renderer-grid",
                type: "grid.task",
                position: Rational.nth(3),
                storage: "parent",
                placement: "child",
              },
              {
                preset: "provider-tasks",
                type: "data-source",
                label: "Tasks",
                lookupKey: ":~lona:tasks",
                meta: { shape: "task/v1", encoding: "task.v1" },
                position: Rational.nth(4),
                storage: "own",
                placement: "child",
                attributes: {
                  dtype: "obj",
                  cellCardinality: "multi",
                },
              },
            ],
          },
        ],
      },
      dynamicTail: {
        parentPreset: "tasks",
        items: () => taskListConfig.taskLists().map(taskListConfigCell),
        as: (item, props, index) => {
          const id = taskListId(item, index);
          return {
            preset: `list-${id}`,
            type: "data-source",
            label: taskListLabel(item, index),
            // Top-level lookupKey so the row's secondary index picks
            // it up — `peekByLookupKey` queries that index. Same fix
            // pattern as the `provider-tasks` static child above.
            lookupKey: taskListLookupKey(item, props, id),
            // Per-list provider rows carry the same canonical `task.v1`
            // shape as the static `:~lona:tasks` provider. Without it
            // `Sheet.encodingForRowId` returns undefined and
            // `SourceRow`'s own fetch skips decoding, so wire cells never
            // materialize as canonical tasks (cf. the calendar tail's
            // `meta.encoding`).
            meta: { shape: "task/v1", encoding: "task.v1" },
            position: Rational.nth(5 + index),
            storage: "own",
            placement: "child",
            attributes: {
              dtype: "obj",
              cellCardinality: "multi",
            },
          };
        },
        filter: (item, local) => {
          const id = taskListId(item, 0);
          return local.visibleByKey.get(id) ?? true;
        },
      },
    },
  };
}

function calendarAliasFactory(): BuiltinAliasFactory {
  return {
    templateName: "alias.calendar",
    template: {
      displayName: "Calendar",
      // Authoring shape (timescale-STABLE — the row structure does not
      // vary by timeScale; only the `calendar-body` render does):
      //   - One `calendar-body` layout row holds the body children
      //     (calendar-list, secondary-timezones, stickers). Its plugin
      //     paints the timed timeline for week/day and the month grid for
      //     month, reading `sheet.timescale` at render — so the body
      //     survives hydration (forking) instead of being swapped per
      //     timescale.
      //   - An all-day strip (`projection [surface=header]`) is always
      //     emitted as a sibling so it pins above the body. The month
      //     render folds all-day events into grid cells and the
      //     `calendar-body` plugin suppresses the strip there; the strip
      //     ROW stays present so the childspec is identical across
      //     timescales.
      //
      // Both `calendar-list` providers (strip + body) fetch from
      // the same lookupKey and rely on cache hits to avoid
      // double-fetching.
      rule: {
        kind: "static",
        children: (_props, ctx) => {
          const hideAllDay =
            Json.boolean(ctx.attributes, "hideAllDayEvents") ?? false;

          const bodyChildren: ChildSpec[] = [
            {
              preset: "calendars",
              type: "alias",
              position: Rational.nth(0),
              storage: "own",
              placement: "child",
              attributes: { template: "alias.calendar-list" },
            },
            {
              preset: "secondary-timezones",
              type: "alias",
              position: Rational.nth(1),
              storage: "own",
              placement: "child",
              attributes: { template: "alias.secondary-timezones" },
            },
          ];

          // Stickers overlay the body as a full-width background subcolumn
          // (`stagger: false`); the inner `:~lona:stickers` source row
          // carries the data + provider. The `calendar-body` plugin renders
          // them only for the timed (non-month) view; month uses grid
          // discovery. Always emitted so the childspec is timescale-stable.
          // See `stickersAliasFactory`.
          //
          // `reconcile: true` so the subcolumn also appears on calendars
          // that were already materialized (authored `hydrated: true` or
          // forked) before this child existed — the hydration reconcile
          // additively synthesizes it under the existing body layout row.
          bodyChildren.push({
            preset: "stickers",
            type: "alias",
            position: Rational.nth(2),
            storage: "own",
            placement: "child",
            reconcile: true,
            attributes: { template: "alias.stickers" },
          });

          const bodyLayout: ChildSpec = {
            preset: "calendar-body",
            type: "calendar-body",
            position: Rational.nth(1),
            storage: "own",
            placement: "child",
            children: bodyChildren,
          };

          // The all-day strip is always hydrated (every timescale);
          // `hideAllDayEvents` toggles its `visible` attribute so the
          // SurfaceProjection drops it from the header WITHOUT dropping the
          // strip rows (events + subscriptions survive a hide/show). In month
          // it is additionally hidden via `hideInTimescales` (below): all-day
          // events fold into the month grid's day cells, so the header strip
          // would be redundant. The strip ROW stays present at every timescale
          // (stable childspec) — only its render-time visibility changes.
          //
          // `reconcile: true` so the strip is additively synthesized on
          // calendars that were already materialized (the lona-js
          // `row-descriptor` Calendar `childSpecs` seeds the body, and the
          // alias arrives `hydrated: true`, so `expandAliasInstance` takes
          // the reconcile path instead of re-running this factory). Without
          // it the all-day projection — and therefore every all-day event
          // bar — never appears on the app's hydrated calendars (matches the
          // `stickers` subcolumn, which is reconciled in for the same reason).
          const allDayProjection: ChildSpec = {
            preset: "all-day",
            type: "projection",
            label: "All-day",
            position: Rational.nth(0),
            storage: "own",
            placement: "child",
            reconcile: true,
            attributes: {
              surface: "header",
              preset: "calendar-all-day",
              visible: !hideAllDay,
              // Hidden in month — all-day events fold into the grid's day
              // cells. The surface projection honors this at render time
              // against the live sheet.timescale (holds across hydration).
              hideInTimescales: ["month"],
            },

            children: [
              {
                preset: "all-day-timeline",
                type: "timeline[row]",
                label: "All-day timeline",
                position: Rational.nth(0),
                storage: "own",
                placement: "child",
                children: [
                  {
                    preset: "all-day-calendars",
                    type: "alias",
                    position: Rational.nth(0),
                    storage: "own",
                    placement: "child",
                    attributes: { template: "alias.calendar-list" },
                  },
                ],
              },
            ],
          };

          return [allDayProjection, bodyLayout];
        },
      },
    },
  };
}

// Standalone all-day strip — emits the same `timeline[row] →
// calendar-list` subtree as `calendarAliasFactory`'s projection
// child, but without a `projection [surface=header]` wrapper, so
// authoring lands it in body by default. Surface placement is
// not structural — any row, including this one, can be
// reparented to any surface (e.g. dragged into the sticky header
// region) at runtime. The two templates differ in their
// authoring defaults, not in their surface destiny.
//
// Use case: a compact "all-day strip" widget on a sheet without
// an hourly timeline grid below it. Decided 2026-05-12: it lands in
// the body surface by default; users can drag it to a header surface.
function calendarAlldayAliasFactory(): BuiltinAliasFactory {
  return {
    templateName: "alias.calendar-allday",
    template: {
      displayName: "All-day events",
      rule: {
        kind: "static",
        children: () => [{
          preset: "strip",
          type: "timeline[row]",
          label: "All-day",
          position: Rational.nth(0),
          storage: "own",
          placement: "child",
          children: [
            {
              preset: "calendars",
              type: "alias",
              position: Rational.nth(0),
              storage: "own",
              placement: "child",
              attributes: {
                template: "alias.calendar-list",
              },
            },
          ],
        }],
      },
    },
  };
}

/**
 * `alias.stickers` — a thin subcolumn-only wrapper so stickers overlay the
 * calendar's timed grid. Its single static child is the canonical
 * `:~lona:stickers` reserved SOURCE row (descriptor `sticker` → `StickerSourcePlugin`),
 * which carries BOTH the data (the reserved lookupKey) and the subcolumn
 * provider. Subcolumn discovery descends THROUGH this alias to that source row;
 * the alias is never the calendar's visible cell, so nothing renders as a row
 * (mirrors how `calendars`/`task-list` contribute subcolumns). The reserved
 * lookupKey sits on a static child inside this stable alias (the `:~lona:tasks`
 * `provider-tasks` pattern), so it materializes once at expansion — no churn.
 *
 * Dependency-free (one fixed reserved row, no per-list config), so it ships in
 * `defaults()` unlike `calendarList`/`taskList`.
 */
function stickersAliasFactory(): BuiltinAliasFactory {
  return {
    templateName: "alias.stickers",
    template: {
      displayName: "Stickers",
      rule: {
        kind: "static",
        children: [
          {
            // NO `meta.encoding`: sticker cells are custom cells read via
            // `customCells` / `parseStickerData`, not a wire decoder. The
            // reserved `:~lona:stickers` row owns sticker cell persistence.
            preset: "provider-stickers",
            type: "sticker",
            label: "Stickers",
            lookupKey: ":~lona:stickers",
            position: Rational.nth(0),
            storage: "own",
            placement: "child",
          },
        ],
      },
    },
  };
}

/**
 * The standard set of pure-static builtin alias factories. These have
 * no app-layer closure — the rows-side ships them as-is. App callers
 * typically combine this with `taskListAliasFactory(taskConfig)`,
 * `calendarListAliasFactory(provider)` and
 * `secondaryTimezonesAliasFactory(prefs)` from lona-js.
 */
function defaultBuiltinAliasFactories(): readonly BuiltinAliasFactory[] {
  return [
    weatherAliasFactory(),
    timezoneAliasFactory(),
    dataAliasFactory(),
    chartAliasFactory(),
    taskAliasFactory(),
    calendarAliasFactory(),
    calendarAlldayAliasFactory(),
    stickersAliasFactory(),
  ];
}

// ── Shared helpers ─────────────────────────────────────────────

/**
 * Mark rows composed into the alias's single vertical `SpanChart`
 * (`ChartAliasPlugin`). Each authors a slot-qualified `_type`
 * (`timeline[column].points`) so it binds under the `timeline[column]` slot;
 * attributes carry only the `color` the alias reads to build each
 * `LineChartBody.Renderer`. Order is draw order: translucent band behind,
 * curve, then points on top.
 */
function chartChildren(): readonly ChildSpec[] {
  return [
    chartMark(
      "chart-band",
      "timeline[column].regression-band",
      "rgba(59, 130, 246, 0.30)",
      0,
    ),
    chartMark(
      "chart-curve",
      "timeline[column].regression-curve",
      "var(--blue)",
      1,
    ),
    chartMark(
      "chart-points",
      "timeline[column].points",
      "var(--text-sec)",
      2,
    ),
  ];
}

function chartMark(
  preset: string,
  rendererId: string,
  color: string,
  index: number,
): ChildSpec {
  return {
    preset,
    type: rendererId,
    position: Rational.nth(index),
    storage: "parent",
    placement: "child",
    attributes: { color },
  };
}

function dataChildren(props: AliasProps, registries: SheetRegistries): readonly ChildSpec[] {
  const lookupKey = requireStringProp(props, "lookupKey", "data");
  const presetSpecs = registries.dataPresets.get(lookupKey) ?? [];
  const dataJson: ChildSpec = {
    preset: "data-json",
    type: "data-json",
    label: "Data",
    lookupKey,
    position: Rational.nth(0),
    storage: "own",
    placement: "child",
  };
  const childSpecs = canonicalizeDataRendererSpecs(
    RowTypeSpecOps.dataPresetToChildSpecs(presetSpecs, null).map((spec, index) => ({
      ...spec,
      position: Rational.nth(index + 1),
    })),
  );
  // When the canvas owns its data (it nests a data-series), nest the raw
  // data-json source under the canvas too. The whole chart then collapses to
  // the canvas row (its data children subsume) instead of showing a "Data"
  // row above it. The canvas still reads the source via row.children().
  const canvas = childSpecs[0];
  if (
    canvas?.type === "data-canvas" &&
    (canvas.children ?? []).some((c) => c.type === "data-series")
  ) {
    const nestedCanvas: ChildSpec = {
      ...canvas,
      position: Rational.nth(0),
      children: [
        ...(canvas.children ?? []),
        { ...dataJson, position: Rational.nth((canvas.children ?? []).length) },
      ],
    };
    return [nestedCanvas, ...childSpecs.slice(1)];
  }
  return [dataJson, ...childSpecs];
}

function canonicalizeDataRendererSpecs(
  specs: readonly ChildSpec[],
): readonly ChildSpec[] {
  return specs.map((spec) => {
    const type = canonicalizeDataRendererSpecType(spec.type);
    const attributes = canonicalizeDataRendererAttributes(spec.attributes);
    const children = spec.children
      ? canonicalizeDataRendererSpecs(spec.children)
      : undefined;
    return {
      ...spec,
      type,
      ...(attributes !== undefined ? { attributes } : {}),
      ...(children !== undefined ? { children } : {}),
    };
  });
}

/**
 * Fold a qualified renderer `_type` down to its catalog kind
 * (`canvas.hrule-all → canvas.hrule`, `canvas.regression-points →
 * canvas.points`, `canvas.bar-chart-with-width|buckets → canvas.bar`). The
 * preset marks author full-fidelity kinds so the graph-props path (which reads
 * the raw preset `type` via `rendererSlotKindOf`) keeps its rendering nuance; the
 * ROW path canonicalizes to the enumerated catalog set here. Non-`canvas`
 * qualified types and unknown kinds pass through unchanged.
 */
function canonicalizeDataRendererSpecType(type: string): string {
  const parsed = parseRendererId(type);
  if (parsed === null || parsed === undefined) return type;
  if (parsed.slot !== "canvas") return type;
  const kind = canonicalCanvasRendererType(parsed.kind);
  if (kind === parsed.kind) return type;
  return `canvas.${kind}`;
}

function canonicalizeDataRendererAttributes(
  attributes: ChildSpec["attributes"],
): ChildSpec["attributes"] {
  if (attributes === undefined) return undefined;
  if (typeof attributes.type !== "string") return attributes;
  const type = canonicalCanvasRendererType(attributes.type);
  if (type === attributes.type) return attributes;
  return { ...attributes, type };
}

function canonicalCanvasRendererType(type: string): string {
  switch (type) {
    case "hrule-all":
      return "hrule";
    case "regression-points":
      return "points";
    case "bar-chart-with-width":
    case "buckets":
      return "bar";
    default:
      return type;
  }
}

/**
 * The single producer of the weather data address (data-address
 * migration Phase A): always the EXPLICIT owner-scoped
 * `[weather@lona.so]:~weather:{lid}` — a `lookupKey` prop in any legacy
 * form (bare `:~weather:{lid}`, user-scoped) is normalized through its
 * parsed location id. A `lookupKey` prop that is not a parseable weather
 * key passes through verbatim (escape hatch for custom/foreign rows).
 */
function weatherLookupKey(props: AliasProps): string {
  const lookupKey = optionalStringProp(props, "lookupKey");
  if (lookupKey != null && !isLonaWeatherAliasKey(lookupKey)) {
    const lid = parseWeatherLocationId(lookupKey);
    if (lid == null) return lookupKey;
    return RowNetworkKey.weatherDataKey(lid);
  }
  const lid = weatherLocationId(props);
  // `""`: no data address (`AliasTemplate.dataRef`'s "none").
  return lid == null ? "" : RowNetworkKey.weatherDataKey(lid);
}

function weatherLogsLookupKey(props: AliasProps): string {
  return `${weatherLookupKey(props)}:logs`;
}

function weatherSourceAttributes(props: AliasProps): NonNullable<ChildSpec["attributes"]> {
  const locationId = weatherLocationId(props);
  const lid = Number(locationId);
  return locationId != null && Number.isFinite(lid) && String(lid) === locationId
    ? { dtype: "obj", lid }
    : { dtype: "obj" };
}

/**
 * The location the alias's own props name (`locationId`, else a weather
 * `lookupKey` prop), or `null`: an alias that names none has none. There
 * is no fallback location here (operator ruling: nothing invented); the
 * user's default location is a preference, read by the
 * preference-driven `alias.default-weather` template, not by this one.
 */
function weatherLocationId(props: AliasProps): Option<string> {
  const locationId = optionalStringProp(props, "locationId");
  if (locationId != null) return locationId;

  const lookupKey = optionalStringProp(props, "lookupKey");
  if (lookupKey != null) {
    const parsed = parseWeatherLocationId(lookupKey);
    if (parsed != null) return parsed;
  }
  return null;
}

/** Whether `lookupKey` (optionally user-scoped) is the `:~lona:weather` alias identity, which names no location. */
function isLonaWeatherAliasKey(lookupKey: string): boolean {
  return stripUserScopePrefix(lookupKey) === ":~lona:weather";
}

function parseWeatherLocationId(lookupKey: string): Option<string> {
  // Strip the optional `[user@host]:` user-scoped prefix — applies to
  // cross-user / multi-tenant lookup keys; the location id sits after
  // the unscoped portion.
  const unscoped = stripUserScopePrefix(lookupKey);
  // Bare `:~weather` / `:weather` keys are deprecated. The `:~lona:weather`
  // alias identity names no location (it is not a data address).
  const prefix = ":~weather:";
  if (!unscoped.startsWith(prefix)) return null;
  const value = unscoped.slice(prefix.length);
  return value.length > 0 ? value : null;
}

function stripUserScopePrefix(lookupKey: string): string {
  if (!lookupKey.startsWith("[")) return lookupKey;
  const close = lookupKey.indexOf("]:");
  return close === -1 ? lookupKey : lookupKey.slice(close + 1);
}

function taskListLookupKey(
  item: CellValue,
  props: AliasProps,
  listId: string,
): string {
  const rec = asRecord(item);
  const lookupKey = rec?.lookupKey;
  if (typeof lookupKey === "string" && lookupKey.length > 0) return lookupKey;
  const provider = rec?.provider;
  if (provider === "apple") {
    return `:~apple:${
      requireStringProp(props, "authId", "task-list")
    }:reminders:${listId}`;
  }
  return `:~google:${
    requireStringProp(props, "authId", "task-list")
  }:tasks:${listId}`;
}

function taskListId(item: CellValue, index: number): string {
  const rec = asRecord(item);
  const id = rec?.id;
  if (typeof id === "string" && id.length > 0) return id;
  return `task-list-${index}`;
}

function taskListLabel(item: CellValue, index: number): string {
  const rec = asRecord(item);
  const title = rec?.title;
  if (typeof title === "string" && title.length > 0) return title;
  const label = rec?.label;
  if (typeof label === "string" && label.length > 0) return label;
  return `Task List ${index + 1}`;
}

function asRecord(
  value: CellValue,
): Option<Readonly<Record<string, CellValue>>> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? value as Readonly<Record<string, CellValue>>
    : null;
}

function requireStringProp(
  props: AliasProps,
  key: string,
  templateName: string,
): string {
  const value = props[key];
  if (typeof value === "string" && value.length > 0) return value;
  throw new Error(
    `Alias template "${templateName}" requires non-empty string prop "${key}".`,
  );
}

function optionalStringProp(props: AliasProps, key: string): Option<string> {
  const value = props[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * Values that go with `BuiltinAliasFactory`: `weather`, `timezone`, `data`,
 * `chart`, `task`, `taskList`, and 4 more.
 */
export namespace BuiltinAliasFactory {
  export const weather = weatherAliasFactory;
  export const timezone = timezoneAliasFactory;
  export const data = dataAliasFactory;
  export const chart = chartAliasFactory;
  export const task = taskAliasFactory;
  export const taskList = taskListAliasFactory;
  export const calendar = calendarAliasFactory;
  export const calendarAllday = calendarAlldayAliasFactory;
  export const stickers = stickersAliasFactory;
  export const defaults = defaultBuiltinAliasFactories;
}
